import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/** Schlüssel, unter dem das gewählte Design im Browser gespeichert wird. */
export const THEME_STORAGE_KEY = 'competa_theme';

/** Verfügbare Designs. */
export type Theme = 'light' | 'dark';

/** Werte, die der Theme-Kontext bereitstellt. */
interface ThemeContextValue {
  /** Aktuell aktives Design. */
  theme: Theme;
  /** Wechselt zwischen hellem und dunklem Design. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Liest das beim Laden gesetzte Design vom Dokument (setzt `public/theme-init.js`).
 * @returns `dark`, wenn das Dokument so markiert ist, sonst `light`.
 */
function readInitialTheme(): Theme {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

/**
 * Stellt das aktuelle Design und den Umschalter bereit. Das Design wird als
 * `data-theme` am `<html>`-Element gesetzt (die Tokens in `tokens.css` reagieren darauf)
 * und – soweit möglich – im Browser gespeichert.
 * @param props.children Die umschlossene Anwendung.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(readInitialTheme);

  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      const next: Theme = current === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // Speichern nicht möglich: Das Design gilt trotzdem für diese Sitzung.
      }
      return next;
    });
  }, []);

  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Zugriff auf Design und Umschalter.
 * @returns Aktuelles Design und `toggleTheme`.
 * @throws Error, wenn kein `ThemeProvider` vorhanden ist.
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme muss innerhalb eines ThemeProvider verwendet werden');
  return context;
}
